import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";

export default function Faqs() {
  const faqs = [
    {
      q: "Do you provide services over the holidays?",
      a: "Yes, Tallahassee Lassie operates 365/24/7. We do charge a holiday fee of $10/visit that goes to your sitter on major holidays. Major holidays include: MLK weekend (Friday-Monday), Memorial Day weekend (Friday-Monday), July 4th and the three days preceding, Labor Day weekend (Friday-Monday), Thanksgiving weekend (Thursday-Sunday), and Christmas Eve through New Year's Day."
    },
    {
      q: "How is my house key/code and/or alarm code secured?",
      a: "We store access codes in the private notes section of the client file. We no longer accept key copies and in situations when we have to, Carla stores them securely."
    },
    {
      q: "What is the rate for meet and greet visits?",
      a: "Meet and Greets are reimbursable! We bill at $30/meet, and then credit to the client's account on their first booking's invoice. If you do not wish to sign up for services, we retain this fee for our time."
    },
    {
      q: "What type of pets do you care for?",
      a: "We care for every pet from exotics to farm animals and everything in between."
    },
    {
      q: "Do I get to meet my pet sitter?",
      a: "We typically have you meet a member of our team and assign sitters based on client and need. If you wish to have a second meet and greet with your sitter, schedule this in your account. There is a cost of $25 for additional meet and greets."
    },
    {
      q: "Can I request a specific pet sitter for services?",
      a: "Yes! In your Time to Pet client account, you may prioritize sitters by preference."
    },
    {
      q: "Do you offer boarding services?",
      a: "No."
    },
    {
      q: "Are you licensed and insured?",
      a: "Yes, Business Insurers of the Carolinas provides our coverage, including bonding, and we are internationally certified through Pet Sitters International comprehensive boards."
    },
    {
      q: "How long have you been in business?",
      a: "We have been operating since 2016."
    },
    {
      q: "How do I schedule and pay for services?",
      a: "There is a mobile app for Time to Pet where you can easily access your account. You may utilize the app for more convenience in scheduling and communication. Please revisit your welcome email for specific Time to Pet instructions."
    },
    {
      q: "Can I tip my sitter?",
      a: "Yes! In your pet profile account, on your invoices, you may scroll to the bottom to \"add tip.\" You can also ask us to email you a copy. Cash tips are also accepted."
    },
    {
      q: "Do you have plans in place in case of inclement weather?",
      a: "Yes. We have a heat guide and a thunderstorm guide. If the county asks us to stay off the roads, you will be contacted and we will have your emergency contact step in for care until it's safe for us to do so. For our heat safety guide, sitters are instructed to stick to shaded areas, avoid asphalt, shorten walks, and provide indoor stimulation. If your length of service is changed, you'll be notified and credited the difference."
    },
    {
      q: "How far in advance should I schedule?",
      a: "Please, schedule as far in advance as possible. We have clients booking into the next calendar year."
    },
    {
      q: "What is your most popular service?",
      a: "The half-hour and forty-five-minute visits are the most popular!"
    },
  ];

  const faqPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <div className="py-16 md:py-24">
      <SEO
        title="FAQs | Frequently Asked Questions"
        description="Frequently asked questions about Tallahassee Lassie Pet Care services including holiday availability, pricing, service areas, pet requirements, and booking policies for dog walking and pet sitting in Tallahassee."
        path="/faqs"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPageJsonLd) }}
      />
      <div className="container mx-auto px-4 max-w-4xl">
        <Reveal>
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-6">Frequently Asked Questions</h1>
            <p className="text-xl text-muted-foreground">
              Everything you need to know about our services, policies, and what makes Tallahassee Lassie different.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="bg-card border border-border p-6 md:p-10 shadow-sm mb-12" style={{ borderRadius: "24px 0 24px 0" }}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left font-serif text-lg font-bold hover:text-primary transition-colors">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base leading-relaxed pt-2 pb-6">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Reveal>

        <Reveal>
          <div className="text-center bg-primary/5 p-10 border border-primary/10" style={{ borderRadius: "24px 0 24px 0" }}>
            <h2 className="text-2xl font-serif font-bold mb-4">Still have questions?</h2>
            <p className="text-muted-foreground mb-8">
              We'd love to chat with you about your pet's specific needs!
            </p>
            <Button asChild size="lg" className="h-14 px-8 text-lg">
              <Link href="/contact-us">
                Contact Us Directly
              </Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
